import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { usePool } from '../context/PoolContext';
import { getPoolMessages, postPoolMessage, getMessageUsers, reportPoolMessage } from '../services/api';
import { supabase, isSupabaseConfigured } from '../services/supabaseClient';
import { requestAndRegisterPush } from '../services/pushNotifications';
import OctaAvatar from '../components/OctaAvatar';
import '../styles/app/chat.css';

const PUSH_PROMPTED_KEY = 'pnp_push_prompted_after_send';
// Users this device has blocked: [{ id, username }]. Their messages are
// hidden here; the block is also sent as a report so it reaches moderation.
const BLOCKED_KEY = 'pnp_chat_blocked_users';

const loadBlocked = () => {
  try {
    const list = JSON.parse(localStorage.getItem(BLOCKED_KEY) || '[]');
    return Array.isArray(list) ? list.filter((u) => u && u.id != null) : [];
  } catch (_) {
    return [];
  }
};

const saveBlocked = (list) => {
  try { localStorage.setItem(BLOCKED_KEY, JSON.stringify(list)); } catch (_) { /* skip */ }
};

const REPORT_REASONS = [
  { id: 'harassment', label: 'Harassment or bullying' },
  { id: 'hate', label: 'Hate speech' },
  { id: 'sexual', label: 'Sexual content' },
  { id: 'spam', label: 'Spam or scams' },
  { id: 'other', label: 'Something else' },
];

// Bottom sheet for message actions (report / block) and the blocked list.
function ModerationSheet({ title, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div className="cc-mod-backdrop" onClick={onClose}>
      <div
        className="cc-mod-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cc-mod-grip" aria-hidden="true" />
        <div className="cc-mod-title">{title}</div>
        {children}
        <button type="button" className="btn btn-ghost btn-block cc-mod-cancel" onClick={onClose}>
          Close
        </button>
      </div>
    </div>,
    document.body
  );
}

// Bubble timestamps are clock-only; the date lives in the day separators.
const formatTime = (iso) => {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const dayKey = (iso) => new Date(iso).toDateString();

const formatDay = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return 'Today';
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  const opts = { weekday: 'short', month: 'short', day: 'numeric' };
  if (d.getFullYear() !== now.getFullYear()) opts.year = 'numeric';
  return d.toLocaleDateString([], opts);
};

// Consecutive messages from one sender within this window share a group.
const GROUP_GAP_MS = 5 * 60 * 1000;

const sameGroup = (a, b) => !!a && !!b
  && a.user_id === b.user_id
  && dayKey(a.created_at) === dayKey(b.created_at)
  && Math.abs(new Date(b.created_at) - new Date(a.created_at)) < GROUP_GAP_MS;

// Stable per-sender tint for names + avatars (red/blue/green/cyan/violet;
// belt gold stays reserved for champions).
const SENDER_TINTS = ['#ff7a6e', '#8db1ff', '#5fe39c', '#5cf0ff', '#c9a4ff', '#ff9f6b', '#7fd8c4'];
const senderTint = (id) => SENDER_TINTS[Math.abs(Number(id) || 0) % SENDER_TINTS.length];

const SendIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M4.5 12h11M11 6.5 16.5 12 11 17.5" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" transform="translate(1.5 0)" />
  </svg>
);

const ChatIcon = () => (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 5.5h14a1.5 1.5 0 0 1 1.5 1.5v8.5A1.5 1.5 0 0 1 19 17h-7.5L7 20.5V17H5a1.5 1.5 0 0 1-1.5-1.5V7A1.5 1.5 0 0 1 5 5.5Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <path d="M8 10.5h8M8 13.5h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

function CornerChat() {
  const { user } = useAuth();
  const { pools, selectedPoolId, setSelectedPoolId, unreadByPool, markChatRead, setActiveChatPoolId } = usePool();
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [blocked, setBlocked] = useState(loadBlocked);
  // { mode: 'actions' | 'report' | 'done' | 'blocked', message? }
  const [sheet, setSheet] = useState(null);
  const [reporting, setReporting] = useState(false);
  const listRef = useRef(null);
  const channelRef = useRef(null);
  const userCacheRef = useRef(new Map());

  const activePool = pools.find((p) => p.id === selectedPoolId) || null;

  const blockedIds = useMemo(() => new Set(blocked.map((u) => String(u.id))), [blocked]);
  const visible = useMemo(
    () => messages.filter((m) => !blockedIds.has(String(m.user_id))),
    [messages, blockedIds]
  );

  const closeSheet = useCallback(() => setSheet(null), []);

  const submitReport = async (message, reason) => {
    setReporting(true);
    try {
      await reportPoolMessage(message.id, reason);
      setSheet({ mode: 'done', message });
    } catch (e) {
      setSheet({ mode: 'done', message, failed: e.response?.data?.error || 'Could not send the report. Try again.' });
    } finally {
      setReporting(false);
    }
  };

  const blockUser = (message) => {
    const next = [
      ...blocked.filter((u) => String(u.id) !== String(message.user_id)),
      { id: message.user_id, username: message.username || 'User' },
    ];
    setBlocked(next);
    saveBlocked(next);
    setSheet(null);
    reportPoolMessage(message.id, 'blocked_user').catch(() => { /* hidden locally either way */ });
  };

  const unblockUser = (id) => {
    const next = blocked.filter((u) => String(u.id) !== String(id));
    setBlocked(next);
    saveBlocked(next);
    if (next.length === 0) setSheet(null);
  };

  const cacheUser = useCallback((id, username, display_name) => {
    if (id == null || !username) return;
    userCacheRef.current.set(id, { username, display_name: display_name || null });
  }, []);

  // Seed cache with the current logged-in user so their own optimistic
  // sends and immediate Realtime echoes always have a name.
  useEffect(() => {
    if (user?.id && user?.username) {
      cacheUser(user.id, user.username, user.display_name);
    }
  }, [user?.id, user?.username, user?.display_name, cacheUser]);

  // Resolve a username for a Realtime payload that arrived without one.
  // Returns the patched message immediately if cached, otherwise fetches
  // and updates state in place when the lookup resolves.
  const enrichSenderName = useCallback(async (msg, applyPatch) => {
    const cached = userCacheRef.current.get(msg.user_id);
    if (cached) return { ...msg, ...cached };
    try {
      const res = await getMessageUsers([msg.user_id]);
      const u = (res.data || [])[0];
      if (u) {
        cacheUser(u.id, u.username, u.display_name);
        applyPatch(msg.id, { username: u.username, display_name: u.display_name });
      }
    } catch (_) { /* leave fallback name */ }
    return msg;
  }, [cacheUser]);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    });
  }, []);

  // Patch helper used by enrichSenderName when a lookup completes after
  // the message has already been added to state.
  const patchMessage = useCallback((id, patch) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  }, []);

  // Tell PoolContext which pool the user is currently viewing so its
  // global Realtime listener auto-marks-read instead of badging.
  // Also fire a one-shot mark-read so the unread pill clears immediately
  // when entering a room with backlog.
  useEffect(() => {
    setActiveChatPoolId(activePool?.id || null);
    if (activePool?.id) markChatRead(activePool.id);
    return () => setActiveChatPoolId(null);
  }, [activePool?.id, setActiveChatPoolId, markChatRead]);

  // Load history whenever the active pool changes
  useEffect(() => {
    if (!activePool) {
      setMessages([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    getPoolMessages(activePool.id)
      .then((res) => {
        if (cancelled) return;
        const list = res.data || [];
        list.forEach((m) => cacheUser(m.user_id, m.username, m.display_name));
        setMessages(list);
        scrollToBottom();
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.response?.data?.error || 'Failed to load messages');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [activePool, scrollToBottom, cacheUser]);

  // Subscribe to realtime inserts for the active pool
  useEffect(() => {
    if (!activePool || !isSupabaseConfigured()) return;

    const channel = supabase
      .channel(`pool-messages-${activePool.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'pool_messages',
          filter: `league_id=eq.${activePool.id}`,
        },
        async (payload) => {
          const raw = payload.new;
          // Realtime payload only includes table columns — no joined username.
          // Try the cache first; fall back to a quick fetch (which patches the
          // message in place when it resolves).
          const cached = userCacheRef.current.get(raw.user_id);
          const m = cached
            ? { ...raw, username: cached.username, display_name: cached.display_name }
            : raw;

          setMessages((prev) => {
            // Skip messages we already added optimistically (matched by id)
            if (prev.some((x) => x.id === m.id)) return prev;
            return [...prev, m];
          });
          scrollToBottom();

          if (!cached) enrichSenderName(raw, patchMessage);
        }
      )
      .subscribe();

    channelRef.current = channel;
    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [activePool, scrollToBottom, enrichSenderName, patchMessage]);

  const send = async (e) => {
    e?.preventDefault();
    const trimmed = body.trim();
    if (!trimmed || !activePool || sending) return;
    setSending(true);
    setError('');
    try {
      const res = await postPoolMessage(activePool.id, trimmed);
      cacheUser(res.data.user_id, res.data.username, res.data.display_name);
      // Realtime will usually deliver this too; the dedupe in the
      // subscription handler keeps it from doubling up.
      setMessages((prev) => {
        if (prev.some((x) => x.id === res.data.id)) return prev;
        return [...prev, res.data];
      });
      setBody('');
      scrollToBottom();
      // Right after the user's first successful chat send, ask the OS for
      // push permission so they get notified about replies. Native-only,
      // and only once per device — see pushNotifications.js for details.
      try {
        if (!localStorage.getItem(PUSH_PROMPTED_KEY)) {
          localStorage.setItem(PUSH_PROMPTED_KEY, '1');
          requestAndRegisterPush();
        }
      } catch (_) { /* localStorage unavailable, skip */ }
    } catch (e2) {
      setError(e2.response?.data?.error || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="container cc-page">
      <div className="cc-shell">
        <header className="cc-head">
          <p className="pnp-eyebrow cc-eyebrow">Pool chat</p>
          <h1 className="pnp-title cc-title">Corner <span className="pnp-punch">Chat</span></h1>
          <p className="cc-sub">
            Talk trash with your pool. Messages auto-delete after 30 days.
          </p>
          <p className="cc-rules">
            Zero tolerance for hate, harassment or sexual content. Tap any message to report it or block the sender.
          </p>
        </header>

        <div className="cc-grid">
          <aside className="cc-pools" aria-label="Your pools">
            <div className="cc-pools-label">Your Pools</div>
            {pools.length === 0 ? (
              <div className="cc-pools-empty">Join a pool to start chatting.</div>
            ) : (
              <div className="cc-pools-list">
                {pools.map((p) => {
                  const unread = unreadByPool[p.id] || 0;
                  const active = p.id === selectedPoolId;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      className={`cc-pool ${active ? 'cc-pool-active' : ''} ${unread > 0 ? 'cc-pool-has-unread' : ''} ${p.is_global ? 'cc-pool-global' : ''}`}
                      aria-pressed={active}
                      onClick={() => setSelectedPoolId(p.id)}
                    >
                      <span className="cc-pool-mark pnp-octagon" aria-hidden="true">
                        {p.is_global
                          ? <img src="/images/title-belt.svg" alt="" />
                          : (p.name || '?').trim().charAt(0).toUpperCase()}
                      </span>
                      <span className="cc-pool-name">{p.name}</span>
                      {unread > 0 && (
                        <span className="cc-pool-unread pnp-num" aria-label={`${unread} unread`}>{unread > 9 ? '9+' : unread}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </aside>

          <section className="cc-room">
            {!activePool ? (
              <div className="cc-room-empty">
                <div className="pnp-empty cc-empty">
                  <div className="pnp-empty-icon"><ChatIcon /></div>
                  <div className="pnp-empty-title">Pick a corner</div>
                  <p>Select a pool to open its Corner Chat.</p>
                </div>
              </div>
            ) : (
              <>
                <header className="cc-room-head">
                  <div className="cc-room-mark pnp-octagon" aria-hidden="true">
                    {activePool.is_global
                      ? <img src="/images/title-belt.svg" alt="" />
                      : (activePool.name || '?').trim().charAt(0).toUpperCase()}
                  </div>
                  <div className="cc-room-titles">
                    <div className="cc-room-title">{activePool.name}</div>
                    <div className="cc-room-meta pnp-num">
                      {visible.length} {visible.length === 1 ? 'message' : 'messages'}
                    </div>
                  </div>
                  {blocked.length > 0 && (
                    <button
                      type="button"
                      className="cc-blocked-btn"
                      onClick={() => setSheet({ mode: 'blocked' })}
                    >
                      {blocked.length} blocked
                    </button>
                  )}
                </header>

                <div className="cc-messages" ref={listRef}>
                  {loading && messages.length === 0 ? (
                    <div className="cc-loading" aria-label="Loading…">
                      <div className="pnp-skel cc-skel cc-skel-l" />
                      <div className="pnp-skel cc-skel cc-skel-l cc-skel-short" />
                      <div className="pnp-skel cc-skel cc-skel-r" />
                      <div className="pnp-skel cc-skel cc-skel-l" />
                      <span className="sr-only">Loading…</span>
                    </div>
                  ) : visible.length === 0 ? (
                    <div className="pnp-empty cc-empty">
                      <div className="pnp-empty-icon"><ChatIcon /></div>
                      <div className="pnp-empty-title">Quiet in here</div>
                      <p>No messages yet. Be the first to talk.</p>
                    </div>
                  ) : (
                    visible.map((m, i) => {
                      const mine = m.user_id === user?.id;
                      const prev = visible[i - 1];
                      const next = visible[i + 1];
                      const newDay = !prev || dayKey(prev.created_at) !== dayKey(m.created_at);
                      const first = !sameGroup(prev, m);
                      const last = !sameGroup(m, next);
                      const tint = senderTint(m.user_id);
                      return (
                        <React.Fragment key={m.id}>
                          {newDay && (
                            <div className="cc-day" role="separator">
                              <span>{formatDay(m.created_at)}</span>
                            </div>
                          )}
                          <div
                            className={`cc-row ${mine ? 'cc-row-mine' : 'cc-row-theirs'} ${first ? 'cc-first' : ''} ${last ? 'cc-last' : ''}`}
                            style={{ '--cc-tint': tint }}
                          >
                            {!mine && (
                              <div className="cc-avatar-slot">
                                {last && (
                                  <OctaAvatar
                                    name={m.username}
                                    size={30}
                                    bg={`linear-gradient(140deg, ${tint}55, rgba(6, 9, 18, 0.9))`}
                                  />
                                )}
                              </div>
                            )}
                            <div className="cc-col">
                              {first && !mine && (
                                <div className="cc-name">{m.username || 'User'}</div>
                              )}
                              <div
                                className={`cc-bubble ${mine ? 'cc-bubble-mine' : 'cc-bubble-actionable'}`}
                                {...(mine ? {} : {
                                  role: 'button',
                                  tabIndex: 0,
                                  'aria-haspopup': 'dialog',
                                  'aria-label': `Message from ${m.username || 'User'}: ${m.body}. Options`,
                                  onClick: () => setSheet({ mode: 'actions', message: m }),
                                  onKeyDown: (e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      setSheet({ mode: 'actions', message: m });
                                    }
                                  },
                                })}
                              >
                                <span className="cc-body">{m.body}</span>
                                <time className="cc-time" dateTime={m.created_at}>{formatTime(m.created_at)}</time>
                              </div>
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })
                  )}
                </div>

                <form className="cc-compose" onSubmit={send}>
                  {error && <div className="cc-error" role="alert">{error}</div>}
                  <div className="cc-compose-bar">
                    <textarea
                      className="cc-input"
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      onKeyDown={onKeyDown}
                      placeholder={`Message ${activePool.name}…`}
                      aria-label={`Message ${activePool.name}`}
                      rows={1}
                      maxLength={2000}
                    />
                    <button
                      type="submit"
                      className={`cc-send ${sending ? 'cc-send-busy' : ''}`}
                      disabled={!body.trim() || sending}
                      aria-label={sending ? 'Sending…' : 'Send'}
                      title={sending ? 'Sending…' : 'Send'}
                    >
                      {sending ? <span className="cc-send-spin" aria-hidden="true" /> : <SendIcon />}
                    </button>
                  </div>
                </form>
              </>
            )}
          </section>
        </div>
      </div>

      {sheet?.mode === 'actions' && (
        <ModerationSheet title={`Message from ${sheet.message.username || 'User'}`} onClose={closeSheet}>
          <p className="cc-mod-quote">{sheet.message.body}</p>
          <div className="cc-mod-actions">
            <button type="button" className="btn btn-secondary btn-block" onClick={() => setSheet({ mode: 'report', message: sheet.message })}>
              Report message
            </button>
            <button type="button" className="btn btn-danger btn-block" onClick={() => blockUser(sheet.message)}>
              Block {sheet.message.username || 'this user'}
            </button>
          </div>
          <p className="cc-mod-note">Blocking hides their messages on this device and alerts our moderators.</p>
        </ModerationSheet>
      )}

      {sheet?.mode === 'report' && (
        <ModerationSheet title="Why are you reporting this?" onClose={closeSheet}>
          <div className="cc-mod-actions">
            {REPORT_REASONS.map((r) => (
              <button
                key={r.id}
                type="button"
                className="btn btn-secondary btn-block cc-mod-reason"
                disabled={reporting}
                onClick={() => submitReport(sheet.message, r.id)}
              >
                {r.label}
              </button>
            ))}
          </div>
        </ModerationSheet>
      )}

      {sheet?.mode === 'done' && (
        <ModerationSheet title={sheet.failed ? 'Report not sent' : 'Thanks for the report'} onClose={closeSheet}>
          {sheet.failed ? (
            <p className="cc-mod-note">{sheet.failed}</p>
          ) : (
            <>
              <p className="cc-mod-note">Our moderators review reports within 24 hours and remove content that breaks the rules.</p>
              <div className="cc-mod-actions">
                <button type="button" className="btn btn-danger btn-block" onClick={() => blockUser(sheet.message)}>
                  Also block {sheet.message.username || 'this user'}
                </button>
              </div>
            </>
          )}
        </ModerationSheet>
      )}

      {sheet?.mode === 'blocked' && (
        <ModerationSheet title="Blocked users" onClose={closeSheet}>
          <ul className="cc-mod-blocked">
            {blocked.map((u) => (
              <li key={u.id}>
                <span>{u.username}</span>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => unblockUser(u.id)}>
                  Unblock
                </button>
              </li>
            ))}
          </ul>
        </ModerationSheet>
      )}
    </div>
  );
}

export default CornerChat;
